import { createContext } from 'react'

import { createTheme, css, PaletteMode } from '@mui/material'
import { deepOrange, grey } from '@mui/material/colors'

// Typography constants
const fontSize = 16
const htmlFontSize = 16

export const pxToRem = (size: number, coef = 1) => `${coef * size / htmlFontSize}rem`

export const sectionGap = {
  xs : pxToRem(65),
  sm:  pxToRem(85),
}

export const headlineSectionGap = {
  xs : pxToRem(30),
  sm:  pxToRem(45),
}

const lightTheme = {
  // palette values for light mode
  primary: {
    main: '#132848',
    contrastText: '#faf7f5',
  },
  secondary: {
    main: '#F6a42A',
  },
  common: {
    white: '#F5F2EE',
  },
  background: {
    default: '#F5F2EE',
    paper: '#faf8f6',
  },
  divider: '#DADADA',
  custom: {
    // Earthy warm palette
    brandpink: '#F4D8CD',      // Sand (warm beige-pink)
    lightpink: '#E8D5C4',      // Soft tan
    black: '#0F0F0F',
    accentyellow: '#FCEDC7',   // Keep (already warm)
    green: '#24682C',
    yellow: '#D4921F',
    lightpurple: '#FAF3E0',    // Warm cream (replaces purple)
    lightgreen: '#D2EAD5',
    lightblue: '#E8D5C4',      // Soft tan (replaces blue)
    pink: '#F4D8CD',           // Sand
    // New earthy colors
    terracotta: '#E07A5F',
    deepTerracotta: '#C65D3B',
    warmCream: '#FAF3E0',
    sand: '#F4D8CD',
    softTan: '#E8D5C4',
    buttonBlueNavy: '#132848',
    shadow: 'rgba(197, 73, 73, 0.2)',
    textButtonShadow: 'rgba(0, 0, 0, 0.05)',
    formFieldBorder: '#F9AC3A',
    formFieldBoxShadow: '0 0px 6px rgba(0, 0, 0, 0.1)',
    NavBarIconName: '#F9AC3A',
    buttonHoverBackground: 'rgba(0, 0, 0, 0.15)',
    footer: {
      text: '#ebeae8',
    },
    input: {
      backgroundColor: '#FDFDFD',
      border: '1px solid #FFD393',
    },
  },
  text: {
    primary: '#1C1C1F',
    secondary: '#383838',
    disabled: '#8E8E93',
  },
  action: {
    hover: 'rgba(0, 0, 0, 0.15)',
  },
}

const darktheme = {
  // palette values for dark mode
  primary: deepOrange,
  divider: deepOrange[700],
  background: {
    default: deepOrange[900],
    paper: deepOrange[900],
  },
  text: {
    primary: '#fff',
    secondary: grey[500],
  },
}

export const getDesignTokens = (mode: PaletteMode) => ({
  sectionGap,
  headlineSectionGap,
  typography: {
    fontFamily: ['Geologica', 'Lora', 'Libre Baskerville'].join(','),
    fontSize,
    htmlFontSize,
    pxToRem: (size: number, coef = 1) => pxToRem(size, coef),
    spacing: (factor: number) => pxToRem(factor * 10),
    h1: {
      fontFamily: ['Geologica', 'Lora', 'Libre Baskerville'].join(','),
      fontSize: pxToRem(24),
      fontWeight: 700,
      letterSpacing: '-0.02em',
      color: mode === 'light' ? lightTheme.text.primary : darktheme.text.primary,
    },
    h2: {
      fontFamily: ['Geologica', 'Lora', 'Libre Baskerville'].join(','),
      fontSize: pxToRem(24),
      fontWeight: 600,
      color: mode === 'light' ? lightTheme.text.primary : darktheme.text.primary,
    },
    h3: {
      fontFamily: ['Geologica', 'Lora', 'Libre Baskerville'].join(','),
      fontSize: pxToRem(20),
      fontWeight: 600,
      color: mode === 'light' ? lightTheme.text.primary : darktheme.text.primary,
    },
    h4: {
      fontFamily: ['Lilita One', 'Geologica', 'Libre Baskerville'].join(','),
      fontSize: pxToRem(18),
      fontWeight: 600,
      color: mode === 'light' ? lightTheme.text.primary : darktheme.text.primary,
    },
    h5: {
      fontFamily: ['Geologica', 'Lora', 'Libre Baskerville'].join(','),
      fontSize: pxToRem(16),
      fontWeight: 600,
      color: mode === 'light' ? lightTheme.text.primary : darktheme.text.primary,
    },
    h6: {
      fontFamily: ['Geologica', 'Lora', 'Libre Baskerville'].join(','),
      fontSize: pxToRem(14),
      fontWeight: 600,
      color: mode === 'light' ? lightTheme.text.primary : darktheme.text.primary,
    },
    body1: {
      fontSize: pxToRem(16),
      fontWeight: 300,
      color: mode === 'light' ? lightTheme.text.primary : darktheme.text.primary,
    },
    body2: {
      fontSize: pxToRem(16),
      fontWeight: 300,
      color: mode === 'light' ? lightTheme.text.primary : darktheme.text.primary,
    },
    button: {
      fontFamily: ['"IBM Plex Sans Thai Looped"', 'Libre Baskerville'].join(','),
      fontSize: pxToRem(14),
      fontWeight: 700,
      textTransform: 'none' as const,
    },
  },
  palette: {
    mode,
    ...(mode === 'light' ? lightTheme : darktheme),
  },
})

export const theme = createTheme(getDesignTokens('light'))

export const ColorModeContext = createContext({ toggleColorMode: () => { } })

export type ThemeType = typeof theme

export const scrollbarStyle = css`
  @media screen and (min-width: 768px) {
    &::-webkit-scrollbar {
      width: 16px;
      height: 16px;
    }

    &::-webkit-scrollbar-thumb {
      background: transparent;
    }

    &:hover::-webkit-scrollbar-thumb {
      background: rgba(0,0,0,0.2);
      border-radius: 10px;
      border: 6px solid transparent;
      background-clip: padding-box;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(0,0,0,0.3);
      border: 4px solid transparent;
      background-clip: padding-box;
    }
  }
`